import { Trans, useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { FaArrowRight, FaArrowUpFromBracket } from 'react-icons/fa6';
import React, { Fragment, useRef } from 'react';
import { MdOutlineUploadFile } from 'react-icons/md';
import { Transition } from '@headlessui/react';
import { Link } from 'react-router-dom';
import { FaExclamationTriangle } from 'react-icons/fa';
import { ExcelColumn } from '../../../../types/misc';
import IconBadge from '../../icons/IconBadge';
import { downloadExcelExample, parseColumns } from '../../../../utils/fileUtils';
import { BusinessInvite } from '../../../../services/model/inviteService.model';
import useWindowDimensions from '../../../../hooks/effects/useWindowDimensions';
import { getSelectedBusiness } from '../../../../hooks/business/useSelectedBusiness';

interface ImportViewHeaderProps {
  invite: BusinessInvite;
}

export function ImportViewHeader({ invite }: ImportViewHeaderProps): JSX.Element {
  const { t } = useTranslation();
  const { width } = useWindowDimensions();
  const isMobile = width < 768;
  const [isOpened, setIsOpened] = React.useState<boolean>(false);

  const downloadExampleFile = () => {
    downloadExcelExample(invite);
    toast.success(t('toast.success.downloadSuccess'));
  };

  return (
    <>
      <div className="mb-2 flex items-center">
        <IconBadge icon={FaArrowUpFromBracket} />

        <div className="ml-4 flex w-full flex-col">
          <h1 className="text-3xl">
            {t('component.modal.importLocalContacts.import.title')}
          </h1>
          <span>
            <Trans
              i18nKey="component.modal.importLocalContacts.import.subtitle"
              className="text-primary-900">
              Je kunt hier contacten importeren met Excel. Download
              <strong className="cursor-pointer underline" onClick={downloadExampleFile}>
                hier
              </strong>
            </Trans>
          </span>
        </div>
      </div>
      <div className="rounded-2xl bg-secondary-200 p-4">
        <p>{t('component.modal.importLocalContacts.import.excelWarning')}</p>
        <Transition
          as={Fragment}
          show={!isMobile || isOpened}
          enter="transform transition duration-200"
          enterFrom="opacity-0 -translate-y-2"
          enterTo="opacity-100 translate-y-0"
          leave="transform transition duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0">
          <ul className="list-inside list-disc">
            <li>
              <span className="font-bold">
                {t('component.modal.importLocalContacts.import.phones')}:
              </span>{' '}
              {t('component.modal.importLocalContacts.import.phoneTip')}
            </li>
            <li>
              <span className="font-bold">{t('dataType.datesOfBirth')}:</span>{' '}
              {t('component.modal.importLocalContacts.import.datesOfBirthTip')}
            </li>
          </ul>
        </Transition>
        {isMobile && (
          <button
            onClick={() => setIsOpened((prev) => !prev)}
            className="font-semibold text-primary-900 underline"
            type="button">
            {isOpened ? t('general.showLess') : t('general.showMore')}...
          </button>
        )}
      </div>
    </>
  );
}

interface ImportViewProps {
  setColumns: (columns: ExcelColumn[] | undefined) => void;
  setFileName: (name: string) => void;
}

export function ImportView({ setColumns, setFileName }: ImportViewProps): JSX.Element {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const business = getSelectedBusiness();

  const uploadExcelFile = async (file: File) => {
    setFileName(file.name);
    const columns = await parseColumns(file).catch(() => {
      toast.error('Er ging iets fout bij het importeren van je bestand');
      return [];
    });

    setColumns(columns);
  };

  const handleFileUpload = (files: FileList) => {
    if (files.length === 1) {
      const file = files[0];
      uploadExcelFile(file);
    } else {
      toast.error(t('toast.error.oneFile'));
    }
  };

  if (business && !business.inviteFields) {
    return (
      <div className="mb-1 flex h-fit w-full flex-col items-center justify-center gap-4 rounded-lg border-2 p-4">
        <FaExclamationTriangle size={24} />
        <span className="items-center justify-center text-center font-medium text-primary-900">
          {t('component.modal.importLocalContacts.import.missingRequestData')}
        </span>
        <Link
          to="/settings/requested-data"
          className="flex items-center gap-1 rounded-md bg-primary-300 px-4 py-2 font-semibold text-primary-900 hover:bg-opacity-75">
          {t('component.modal.importLocalContacts.import.toRequestedData')}
          <FaArrowRight size={14} />
        </Link>
      </div>
    );
  }

  return (
    <>
      <div
        onDrop={(e) => {
          e.preventDefault();
          handleFileUpload(e.dataTransfer.files);
        }}
        onClick={() => inputRef.current?.click()}
        className="mb-1 flex h-fit w-full cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-primary-900 p-4 hover:border-opacity-80 hover:bg-primary-300 hover:bg-opacity-10">
        <MdOutlineUploadFile className="mb-1 h-14 w-14 text-primary-900" />
        <span className="w-1/4 items-center justify-center text-center text-sm font-medium text-primary-900">
          <Trans i18nKey="component.modal.importLocalContacts.import.importButton" />
        </span>
      </div>
      <span className="text-xs font-medium text-primary-900">
        {t('component.modal.importLocalContacts.import.fileSupported')}
      </span>
      <input
        type="file"
        className="hidden"
        onChange={(e) => handleFileUpload(e.target.files!)}
        accept=".xls, .xlsx, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        ref={inputRef}
      />
    </>
  );
}
